import React, {useState, useEffect} from 'react';
import {Trans} from 'react-i18next';
import {toast} from 'react-toastify';
import ActionButton from '../../../../../components/datatable/ActionButton';
import skincareProDao from '../../../../../dao/skincareProDao';
import {useSelector} from 'react-redux';
import FormattedDate from '../../../../../components/product/FormattedDate';
import {
  DateRangeFilter,
  TextFilter,
} from '../../../../../components/datatable/DataFilter';
import AddOrEditCategoryManagement from '../addoreditcategorymanagement';
import DataTable from '../../../../../components/datatable/DataTable';
import _get from 'lodash/get';
import InfoDialog from '../../../../../components/dialogs/InfoDialog';
import smbUtils from '../../../../../utils/smbUtils';

const getCategoryType = pathname => {
  if (pathname.includes('list-product-category')) {
    return 'Skincare';
  }
  if (pathname.includes('list-treatment-category')) {
    return 'Treatment';
  }
  return null;
};

const ControlBar = ({setShowModal}) => {
  return (
    <div
      className="control-bar"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: '10px',
      }}
    >
      <button
        type="button"
        className="btn btn-danger kt-margin-b-10"
        onClick={() => {
          setShowModal(true);
        }}
      >
        <i className="fa fa-plus" />
        <Trans i18nKey="p.s.listcategory.add.category">Add Category</Trans>
      </button>
    </div>
  );
};

export default function ListCategory() {
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [editData, setEditData] = useState({});
  const {token, customerId} = smbUtils.customerInfo(
    useSelector(state => state.components.auth.accountSummary)
  );
  const pathname = new URL(window.location.href).pathname;
  const categoryType = getCategoryType(pathname);

  const listCategoryAPI = async () => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('types', categoryType);

    await skincareProDao
      .getCategoryList(tmp)
      .then(res => {
        if (res.categories) {
          setOriginalData(res.categories);
          setIsLoading(false);
          return;
        }
        setTotalSize(_get(res, 'totalSize', 0));
      })
      .catch(err => {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        setTotalSize(0);
      });
    setIsLoading(false);
  };

  const delCategoryAPI = async (categoryId, categoryName) => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('type', categoryType);
    tmp.append('categoryId', categoryId);
    const res = await skincareProDao.deleteCategoryList(tmp);

    if (res.status && res.status === 'success') {
      toast.success(
        <Trans i18nKey="toast.success.delete.product">Deleted!</Trans>
      );
      setIsLoading(true);
      await listCategoryAPI();
      setIsLoading(false);
      return;
    }

    toast.error(
      <Trans i18nKey="toast.error.delete.product">
        Failed to delete {{categoryName}}.
      </Trans>
    );
    setIsLoading(false);
  };

  const handleCloseModel = async () => {
    setEditData({});
    setShowModal(false);
    setIsLoading(true);
    await listCategoryAPI();
    setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);
    listCategoryAPI();
  }, []);

  const listCategoryColumns = [
    {
      Header: (
        <InfoDialog
          trigger={'click'}
          placement="right"
          title={
            <>
              <i className="fa fa-question-circle" />{' '}
              <Trans i18nKey="p.s.listcategory.category.name">
                Category Name
              </Trans>
            </>
          }
          description={
            <Trans i18nKey="p.s.listcategory.category.Name.desc">
              Names are displayed in default language.
            </Trans>
          }
        >
          <label className="cursor-help">
            <Trans i18nKey="p.s.listcategory.category.name">
              Category Name
            </Trans>{' '}
            <i className="fa fa-question-circle" />
          </label>
        </InfoDialog>
      ),
      accessor: 'name',
      disableSortBy: true,
      style: {width: '20%', textAlign: 'center', wordBreak: 'break-word'},
      Filter: TextFilter,
    },
    {
      Header: <Trans i18nKey="p.s.listcategory.creation">Creation</Trans>,
      accessor: 'createdTime',
      disableSortBy: false,
      Filter: DateRangeFilter,
      style: {width: '25%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div className="text-right">
          <FormattedDate time={value} type={'long'} />
        </div>
      ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.listcategory.modification">Modification</Trans>
      ),
      accessor: 'lastModified',
      disableSortBy: false,
      Filter: DateRangeFilter,
      style: {width: '25%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div className="text-right">
          <FormattedDate time={value} type={'long'} />
        </div>
      ),
    },
    {
      Header: <Trans i18nKey="p.s.listcategory.action">Actions</Trans>,
      id: 'actions',
      accessor: 'id',
      disableSortBy: true,
      style: {width: '20%', textAlign: 'center'},
      Cell: ({
        cell: {
          row: {original},
        },
      }) => (
        <div
          className="text-center"
          style={{display: 'flex', justifyContent: 'center'}}
        >
          <ActionButton
            type={'edit'}
            onClick={() => {
              setEditData({id: original.id, names: original.names});
              setShowModal(true);
            }}
          />
          <ActionButton
            type={'delete'}
            onClick={async () => {
              await delCategoryAPI(original.id, original.name);
            }}
            disabled={!original.deletable}
            disableDesc={
              <Trans i18nKey="p.s.listcategory.delete.error.msg">
                This Category is being used by some product. You cannot delete
                the this category until the corresponding product has removed
                the linkage of this category.
              </Trans>
            }
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="portlet-title">
        {categoryType === 'Skincare' ? (
          <h4>
            <Trans i18nKey="p.s.listcategory.product.title">
              Product Category Management
            </Trans>
          </h4>
        ) : (
          <h4>
            <Trans i18nKey="p.s.listcategory.treatment.title">
              Treatment Category Management
            </Trans>
          </h4>
        )}
      </div>
      <div className="portlet-control">
        <ControlBar setShowModal={setShowModal} />
      </div>
      <div className="portlet-body" style={{overflowX: 'scroll'}}>
        <AddOrEditCategoryManagement
          editData={editData}
          showModal={showModal}
          handleCloseModel={handleCloseModel}
        />
        <DataTable
          loading={isLoading}
          columns={listCategoryColumns}
          data={originalData}
          totalSize={totalSize}
          initState={{
            sortBy: {id: 'lastModified', desc: true},
            pageSize: 10,
          }}
          customTableStyle={{overflowX: 'scroll'}}
        />
      </div>
    </>
  );
}
